import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product } from '@/api/products';
import { cn } from '@/lib/utils';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { DirectSaleMockupPreview } from '../DirectSaleMockupPreview';

interface DirectSaleColorPickerProps {
  product: Product;
  /** The colour that is chosen. */
  colorId: string | null;
  /** Called on every click, also on the chosen colour: the parent decides. */
  onPick: (colorId: string) => void;
}

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

/**
 * The colours of the desktop purchase column: the name of the chosen colour
 * and one thumb per colour. Hovering or focusing a thumb names that colour
 * instead, without choosing it.
 */
export function DirectSaleColorPicker({
  product,
  colorId,
  onPick,
}: DirectSaleColorPickerProps) {
  const { t } = useTranslation();
  const activePosition = useProductSelectionStore((s) => s.activePosition);
  const [previewedColorId, setPreviewedColorId] = useState<string | null>(null);

  const colors = product.colors ?? [];
  if (colors.length === 0) return null;

  const selectedColor = colors.find((c) => c.id === colorId) ?? null;
  const namedColor =
    colors.find((c) => c.id === previewedColorId) ?? selectedColor;
  // The colour thumbs show the view the customer is looking at; without
  // one, the first view that is offered at all.
  const thumbSlug =
    product.positions?.find((p) => p.slug === activePosition)?.slug ??
    product.positions?.find((p) => p.printSurcharge != null)?.slug ??
    product.positions?.[0]?.slug ??
    null;

  return (
    <div className="mt-5">
      <p className="text-[13px] tracking-wide text-ink-2">
        <span className="uppercase">{t('directSale.design.color')}</span>
        {': '}
        <span className="font-bold text-ink">{namedColor?.name}</span>
      </p>
      <div className="mt-2 grid grid-cols-6 gap-1.5 min-[1200px]:grid-cols-8">
        {colors.map((c) => (
          <button
            key={c.id}
            type="button"
            aria-label={c.name}
            aria-pressed={c.id === colorId}
            onClick={() => onPick(c.id)}
            onMouseEnter={() => setPreviewedColorId(c.id)}
            onMouseLeave={() => setPreviewedColorId(null)}
            onFocus={() => setPreviewedColorId(c.id)}
            onBlur={() => setPreviewedColorId(null)}
            className={cn(
              // The product alone, no tile behind it: a grid of tiles reads
              // heavier than the shirts themselves. The chosen one gets a box.
              'flex aspect-square items-center justify-center rounded-control border-2 p-0.5 transition-colors hover:bg-paper-2',
              focusRing,
              c.id === colorId ? 'border-ink' : 'border-transparent'
            )}
          >
            {/* The product in each colour, not a swatch: a hex dot says
                little about a heathered grey. */}
            {thumbSlug ? (
              <DirectSaleMockupPreview
                product={product}
                positionSlug={thumbSlug}
                colorId={c.id}
                designDataUrl={null}
                fit={1}
                lazyLoad
                className="h-[46px] w-[46px]"
              />
            ) : (
              <span
                className="block h-[46px] w-[46px] rounded-control"
                style={{ backgroundColor: c.hexCode || '#e5e7eb' }}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
